import React, { useEffect, useRef } from 'react';
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import Image from '@editorjs/image';
import Code from '@editorjs/code';
import Quote from '@editorjs/quote';
import Checklist from '@editorjs/checklist';
import Table from '@editorjs/table';
import LinkTool from '@editorjs/link';
import EditorJsColumns from '@calumk/editorjs-columns';
import Embed from '@editorjs/embed';
import Marker from '@editorjs/marker';
import InlineCode from '@editorjs/inline-code';
import Underline from '@editorjs/underline';
import Delimiter from '@editorjs/delimiter';
import Warning from '@editorjs/warning';
import MentionTool from 'editorjs-mention-tool';
import 'editorjs-mention-tool/src/styles.css';

import { notesAPI } from '../../api/notesApi';
import { tokenManager, API_BASE_URL } from '../../api/api';
import useIsMobile from '../../hooks/useIsMobile';

const decorateMentionText = (value) => {
    if (typeof value !== 'string' || !value) return value;

    // &#8203; (HTML entity) → \u200B (gerçek karakter) normalleştirmesi;
    // her iki form da kayıtlarda görünebilir.
    const normalized = value.replace(/&#8203;/g, '\u200B');

    // Mevcut mention span'larını geçici işaretçilerle değiştir (yeniden sarmalamayı önler),
    // kalan düz metindeki @mention'ları dekore et, sonra span'ları geri yükle.
    const placeholders = [];
    let pIdx = 0;
    const withPlaceholders = normalized.replace(
        /<span\b[^>]*\bclass="[^"]*mention_tool_at[^"]*"[^>]*>[^<]*<\/span>/g,
        (match) => {
            placeholders.push(match);
            return `\x00M${pIdx++}\x00`;
        }
    );

    // \u200B (mention sonrası sıfır genişlikli boşluk) geçerli bir önek olarak kabul edilir.
    const decorated = withPlaceholders.replace(
        /(^|[\s>\u200B])@([A-Za-z0-9_.-]+)/g,
        (match, prefix, username) =>
            `${prefix}<span class="mention_tool_at" contenteditable="false" data-mention="${username}">@${username}</span>\u200B`
    );

    return placeholders.length === 0
        ? decorated
        : decorated.replace(/\x00M(\d+)\x00/g, (_, i) => placeholders[parseInt(i, 10)]);
};

const decorateMentionsInData = (editorData) => {
    if (!editorData || !Array.isArray(editorData.blocks)) return editorData;

    return {
        ...editorData,
        blocks: editorData.blocks.map((block) => {
            if (!block || typeof block !== 'object') return block;
            const data = block.data || {};
            const nextData = { ...data };

            if (typeof nextData.text === 'string') {
                nextData.text = decorateMentionText(nextData.text);
            }

            if (Array.isArray(nextData.items)) {
                nextData.items = nextData.items.map((item) => {
                    if (typeof item === 'string') return decorateMentionText(item);
                    if (item && typeof item === 'object' && typeof item.text === 'string') {
                        return { ...item, text: decorateMentionText(item.text) };
                    }
                    return item;
                });
            }

            return { ...block, data: nextData };
        })
    };
};

const NotesEditor = ({ data, onChange, readOnly = false, mentionUsers = [] }) => {
    const isMobile = useIsMobile();
    const editorRef = useRef(null);
    const editorInstance = useRef(null);
    const mentionsInitializedRef = useRef(false);
    const mentionToolRef = useRef(null);
    const holderIdRef = useRef(`notes-editor-holder-${Math.random().toString(36).slice(2, 9)}`);
    const keyupHandlerRef = useRef(null);
    const keydownHandlerRef = useRef(null);
    const mentionSearchHandlerRef = useRef(null);

    const normalizeCaretIfInsideMention = () => {
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;

        const range = sel.getRangeAt(0);
        let node = range.startContainer;
        if (!node) return;

        if (node.nodeType === Node.TEXT_NODE) {
            node = node.parentElement;
        }

        const mentionEl = node?.closest?.('.mention_tool_at');
        if (!mentionEl || !editorRef.current?.contains(mentionEl)) return;

        const parent = mentionEl.parentNode;
        if (!parent) return;

        let nextNode = mentionEl.nextSibling;
        if (!nextNode || nextNode.nodeType !== Node.TEXT_NODE) {
            nextNode = document.createTextNode('\u200B');
            parent.insertBefore(nextNode, mentionEl.nextSibling);
        }

        const newRange = document.createRange();
        newRange.setStart(nextNode, nextNode.textContent?.length || 0);
        newRange.collapse(true);

        sel.removeAllRanges();
        sel.addRange(newRange);
    };

    const initializeMentionTool = () => {
        if (readOnly || mentionsInitializedRef.current || !editorRef.current || !mentionUsers.length) {
            return;
        }

        const mappedUsers = mentionUsers.map((user) => {
            const firstName = user.first_name || '';
            const lastName = user.last_name || '';
            const fullName = user.full_name || `${firstName} ${lastName}`.trim() || user.email || 'User';
            const emailPrefix = (user.email || '').split('@')[0] || '';
            const fallbackSlug = `${firstName}.${lastName}`.replace(/\.+/g, '.').replace(/^\.+|\.+$/g, '');
            const slug = (emailPrefix || fallbackSlug || fullName)
                .toLowerCase()
                .replace(/\s+/g, '.');

            return {
                id: String(user.id),
                name: fullName,
                slug
            };
        });

        const mentionTool = new MentionTool({
            holder: holderIdRef.current,
            accessKey: '@',
            allUsers: mappedUsers,
            baseUrl: '',
            searchAPIUrl: ''
        });
        mentionToolRef.current = mentionTool;

        // TR klavye gibi durumlarda e.key '@' olmayabilir (AltGr+Q).
        // Bu nedenle caret öncesindeki gerçek karakter '@' ise dropdown'u manuel aç.
        const keyupHandler = () => {
            const activeEl = document.activeElement;
            if (!activeEl || !editorRef.current?.contains(activeEl)) return;
            if (!mentionToolRef.current) return;

            try {
                const sel = window.getSelection();
                if (!sel || !sel.rangeCount) return;
                const range = sel.getRangeAt(0);
                const selectedNode = range.endContainer;
                const caretPos = range.endOffset;
                const nodeText = selectedNode?.textContent || '';
                const prevChar = caretPos > 0 ? nodeText[caretPos - 1] : '';

                mentionToolRef.current.prevActiveElement = activeEl;
                mentionToolRef.current.prevCaretPosAndSelectedNode = {
                    caretPos,
                    selectedNode
                };

                if (prevChar === '@') {
                    mentionToolRef.current.showUserMentionToolbar();
                }
            } catch (e) {
                // no-op: selection API bazı edge-case'lerde hata verebilir
            }
        };

        keyupHandlerRef.current = keyupHandler;
        editorRef.current.addEventListener('keyup', keyupHandler);

        const keydownHandler = () => {
            // İmleç mention içine düşerse yazmadan önce mention dışına taşı.
            normalizeCaretIfInsideMention();
        };
        keydownHandlerRef.current = keydownHandler;
        editorRef.current.addEventListener('keydown', keydownHandler);

        // Paketin kendi local araması listeyi güncellemediği için
        // arama kutusunu biz filtreliyoruz.
        const toolbarEl = mentionTool.nodes?.userMentionToolbar;
        const searchInput = toolbarEl?.querySelector('.search-textbox');
        const applySearchFilter = () => {
            if (!toolbarEl) return;
            const q = (searchInput?.value || '')
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .trim();

            const items = toolbarEl.querySelectorAll('.user-list-item-wrapper');
            items.forEach((item) => {
                const name = (item.querySelector('.user-full-name')?.textContent || '')
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '');
                const slug = (item.querySelector('.user-slug')?.textContent || '')
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '');

                const matched = !q || name.includes(q) || slug.includes(q);
                item.style.display = matched ? 'flex' : 'none';
            });
        };

        mentionSearchHandlerRef.current = applySearchFilter;
        if (searchInput) {
            searchInput.addEventListener('input', applySearchFilter);
            searchInput.addEventListener('keyup', applySearchFilter);
            searchInput.addEventListener('focus', applySearchFilter);
        }

        mentionsInitializedRef.current = true;
    };

    // Initialize Editor
    useEffect(() => {
        if (!editorRef.current) return;
        if (editorInstance.current) return;

        const initEditor = () => {
            const initialData = decorateMentionsInData(data || { blocks: [] });
            const tools = {
                header: {
                    class: Header,
                    inlineToolbar: true,
                    config: {
                        placeholder: 'Başlık',
                        levels: [1, 2, 3],
                        defaultLevel: 2
                    }
                },
                list: {
                    class: List,
                    inlineToolbar: true,
                    config: { defaultStyle: 'unordered' }
                },
                checklist: {
                    class: Checklist,
                    inlineToolbar: true,
                },
                image: {
                    class: Image,
                    config: {
                        endpoints: {
                            byFile: `${API_BASE_URL}/notes/upload-image/`,
                            byUrl: `${API_BASE_URL}/notes/upload-image/`,
                        },
                        additionalRequestHeaders: {
                            Authorization: `Bearer ${tokenManager.getAccessToken()}`
                        },
                        uploader: {
                            // Dosyayı base64'e çevirip öyle gönderiyoruz (En kararlı yöntem)
                            uploadByFile(file) {
                                return new Promise((resolve) => {
                                    const reader = new FileReader();
                                    reader.onload = async (e) => {
                                        try {
                                            const response = await notesAPI.uploadImageByUrl(e.target.result);
                                            resolve(response);
                                        } catch (err) {
                                            console.error("Upload failed", err);
                                            resolve({ success: 0 });
                                        }
                                    };
                                    reader.readAsDataURL(file);
                                });
                            },
                            async uploadByUrl(url) {
                                if (url.startsWith('file://')) return { success: 0 };
                                try {
                                    return await notesAPI.uploadImageByUrl(url);
                                } catch (err) {
                                    return { success: 0 };
                                }
                            }
                        }
                    }
                },
                quote: {
                    class: Quote,
                    inlineToolbar: true,
                    config: {
                        quotePlaceholder: 'Alıntı metni...',
                        captionPlaceholder: 'Kaynak',
                    },
                },
                code: Code,
                linkTool: LinkTool,
                marker: Marker,
                inlineCode: InlineCode,
                underline: Underline,
                delimiter: Delimiter,
                warning: Warning,
                ...(isMobile ? {} : {
                    table: Table,
                    embed: Embed,
                    columns: {
                        class: EditorJsColumns,
                        config: {
                            tools: {
                                header: Header,
                                list: List,
                                checklist: Checklist,
                                code: Code,
                                image: {
                                    class: Image,
                                    config: {
                                        endpoints: {
                                            byFile: `${API_BASE_URL}/notes/upload-image/`,
                                            byUrl: `${API_BASE_URL}/notes/upload-image/`,
                                        },
                                        additionalRequestHeaders: {
                                            Authorization: `Bearer ${tokenManager.getAccessToken()}`
                                        },
                                        uploader: {
                                            uploadByFile(file) {
                                                return new Promise((resolve) => {
                                                    const reader = new FileReader();
                                                    reader.onload = async (e) => {
                                                        const response = await notesAPI.uploadImageByUrl(e.target.result);
                                                        resolve(response);
                                                    };
                                                    reader.readAsDataURL(file);
                                                });
                                            },
                                            async uploadByUrl(url) {
                                                if (url.startsWith('file://')) return { success: 0 };
                                                return await notesAPI.uploadImageByUrl(url);
                                            }
                                        }
                                    }
                                },
                                quote: Quote,
                                table: Table,
                                embed: Embed,
                                marker: Marker,
                                inlineCode: InlineCode,
                                underline: Underline,
                                delimiter: Delimiter,
                                warning: Warning,
                            },
                            EditorJsLibrary: EditorJS,
                        }
                    },
                }),
            };

            const editor = new EditorJS({
                holder: editorRef.current,
                readOnly: readOnly,
                data: initialData,
                placeholder: isMobile
                    ? 'Yazmaya başla...'
                    : 'Yazmaya başlamak için tıklayın veya "/" tuşuna basın...',
                tools,

                onChange: async (api) => {
                    if (onChange) {
                        const savedData = await api.saver.save();
                        onChange(savedData);
                    }
                },
            });

            editorInstance.current = editor;
            initializeMentionTool();
        };

        initEditor();

        return () => {
            if (editorInstance.current && editorInstance.current.destroy) {
                editorInstance.current.destroy();
                editorInstance.current = null;
            }
            if (editorRef.current && keyupHandlerRef.current) {
                editorRef.current.removeEventListener('keyup', keyupHandlerRef.current);
            }
            if (editorRef.current && keydownHandlerRef.current) {
                editorRef.current.removeEventListener('keydown', keydownHandlerRef.current);
            }
            if (mentionToolRef.current?.nodes?.userMentionToolbar && mentionSearchHandlerRef.current) {
                const searchInput = mentionToolRef.current.nodes.userMentionToolbar.querySelector('.search-textbox');
                if (searchInput) {
                    searchInput.removeEventListener('input', mentionSearchHandlerRef.current);
                    searchInput.removeEventListener('keyup', mentionSearchHandlerRef.current);
                    searchInput.removeEventListener('focus', mentionSearchHandlerRef.current);
                }
            }
            mentionsInitializedRef.current = false;
            mentionToolRef.current = null;
            keyupHandlerRef.current = null;
            keydownHandlerRef.current = null;
            mentionSearchHandlerRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile]);

    useEffect(() => {
        initializeMentionTool();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mentionUsers, readOnly]);

    return (
        <div className="prose dark:prose-invert max-w-none w-full">
            <div id={holderIdRef.current} ref={editorRef} className="min-h-[320px] sm:min-h-[500px] py-3 sm:py-4" />
        </div>
    );
};

export default NotesEditor;
